import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import GpsFreshness from '../components/GpsFreshness';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import { 
    Plus, 
    Truck,
    MapPin,
    Edit,
    Trash2,
    UserPlus,
    UserMinus,
    Navigation,
    AlertTriangle,
    Wrench,
    Package,
    CheckCircle,
    XCircle,
    ClipboardList
} from 'lucide-react';
import { toast } from 'sonner';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const vehicleTypes = [
    { value: 'ambulance', label: 'Ambulance' },
    { value: 'utilitaire', label: 'Utilitaire' },
    { value: 'bus', label: 'Bus' },
    { value: 'voiture', label: 'Voiture' },
    { value: 'camion', label: 'Camion' },
    { value: '4x4', label: '4x4' }
];

const statusConfig = {
    disponible: { label: 'Disponible', color: 'bg-green-500/20 text-green-400 border-green-500/30', dot: '#22c55e' },
    en_mission: { label: 'En mission', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', dot: '#f59e0b' },
    en_transit: { label: 'En transit', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', dot: '#3b82f6' },
    maintenance: { label: 'En maintenance', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', dot: '#f97316' },
    en_panne: { label: 'En panne', color: 'bg-red-500/20 text-red-400 border-red-500/30', dot: '#ef4444' },
    reapprovisionnement: { label: 'Reapprovisionnement', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', dot: '#a855f7' }
};

const getStatusConfig = (status) => statusConfig[status] || statusConfig.disponible;

const VehiclesPage = () => {
    const { token, canManageVehicles, user: currentUser } = useAuth();
    const { theme } = useTheme();
    const [vehicles, setVehicles] = useState([]);
    const [registeredUsers, setRegisteredUsers] = useState([]);
    const [materialRequests, setMaterialRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [personnelDialogOpen, setPersonnelDialogOpen] = useState(false);
    const [locationDialogOpen, setLocationDialogOpen] = useState(false);
    const [missionDialogOpen, setMissionDialogOpen] = useState(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [problemDialogOpen, setProblemDialogOpen] = useState(false);
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState(null);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [selectedDriverId, setSelectedDriverId] = useState('');
    const [editingVehicle, setEditingVehicle] = useState(null);
    const [locationData, setLocationData] = useState({ gps_latitude: '', gps_longitude: '', manual_location: '' });
    const [missionText, setMissionText] = useState('');
    const [problemData, setProblemData] = useState({ description: '', severity: 'maintenance' });
    const [newStatus, setNewStatus] = useState('');
    
    const [formData, setFormData] = useState({
        name: '',
        type: 'voiture',
        plate_number: '',
        capacity: 4,
        notes: ''
    });

    const headers = { Authorization: `Bearer ${token}` };

    const fetchVehicles = async () => {
        try {
            const [vehiclesRes, usersRes, requestsRes] = await Promise.all([
                axios.get(`${API}/vehicles`, { headers }),
                axios.get(`${API}/users`, { headers }).catch(() => ({ data: [] })),
                axios.get(`${API}/material-requests`, { headers }).catch(() => ({ data: [] }))
            ]);
            setVehicles(vehiclesRes.data);
            setRegisteredUsers(usersRes.data.filter(u => u.role !== 'pending'));
            setMaterialRequests(requestsRes.data);
        } catch (error) {
            console.error('Failed to fetch vehicles:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVehicles();
        const interval = setInterval(fetchVehicles, 30000);
        return () => clearInterval(interval);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingVehicle) {
                await axios.put(`${API}/vehicles/${editingVehicle.id}`, formData, { headers });
                toast.success('Vehicule modifie');
            } else {
                await axios.post(`${API}/vehicles`, formData, { headers });
                toast.success('Vehicule ajoute');
            }
            setDialogOpen(false);
            resetForm();
            setEditingVehicle(null);
            fetchVehicles();
        } catch (error) {
            toast.error("Erreur lors de l'enregistrement");
        }
    };

    const handleEditVehicle = (vehicle) => {
        setEditingVehicle(vehicle);
        setFormData({
            name: vehicle.name,
            type: vehicle.type,
            plate_number: vehicle.plate_number,
            capacity: vehicle.capacity,
            notes: vehicle.notes || ''
        });
        setDialogOpen(true);
    };

    const handleChangeStatus = async () => {
        if (!selectedVehicle || !newStatus) return;
        try {
            const data = { status: newStatus };
            if (newStatus === 'disponible') {
                data.current_mission = '';
            }
            await axios.put(`${API}/vehicles/${selectedVehicle.id}`, data, { headers });
            toast.success('Statut mis a jour');
            setStatusDialogOpen(false);
            setNewStatus('');
            fetchVehicles();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handleSetMission = async () => {
        if (!selectedVehicle) return;
        try {
            await axios.put(`${API}/vehicles/${selectedVehicle.id}`, {
                status: 'en_mission',
                current_mission: missionText
            }, { headers });
            toast.success('Mission assignee');
            setMissionDialogOpen(false);
            setMissionText('');
            fetchVehicles();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handleReportProblem = async () => {
        if (!selectedVehicle || !problemData.description) return;
        try {
            await axios.post(`${API}/vehicles/${selectedVehicle.id}/report-problem`, problemData, { headers });
            toast.success('Probleme declare');
            setProblemDialogOpen(false);
            setProblemData({ description: '', severity: 'maintenance' });
            fetchVehicles();
        } catch (error) {
            toast.error('Erreur lors de la declaration');
        }
    };

    const handleResolveProblem = async (vehicleId) => {
        try {
            await axios.put(`${API}/vehicles/${vehicleId}/resolve-problem`, {}, { headers });
            toast.success('Probleme resolu');
            fetchVehicles();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handleUpdateLocation = async () => {
        if (!selectedVehicle) return;
        try {
            const data = {};
            if (locationData.gps_latitude && locationData.gps_longitude) {
                data.gps_latitude = parseFloat(locationData.gps_latitude);
                data.gps_longitude = parseFloat(locationData.gps_longitude);
            }
            if (locationData.manual_location) data.manual_location = locationData.manual_location;
            await axios.put(`${API}/vehicles/${selectedVehicle.id}/update-location`, data, { headers });
            toast.success('Position mise a jour');
            setLocationDialogOpen(false);
            setLocationData({ gps_latitude: '', gps_longitude: '', manual_location: '' });
            fetchVehicles();
        } catch (error) {
            toast.error('Erreur de mise a jour');
        }
    };

    const handleAddPersonnel = async () => {
        if (!selectedVehicle) return;
        if (!selectedDriverId && selectedUserIds.length === 0) return;
        try {
            // Assign driver first
            if (selectedDriverId) {
                const driver = registeredUsers.find(x => x.id === selectedDriverId);
                if (driver) {
                    await axios.put(`${API}/vehicles/${selectedVehicle.id}/assign-personnel`, {
                        personnel_name: driver.name, personnel_id: driver.id, role: driver.role, is_driver: true
                    }, { headers });
                }
            }
            // Then passengers
            for (const userId of selectedUserIds) {
                if (userId === selectedDriverId) continue;
                const u = registeredUsers.find(x => x.id === userId);
                if (!u) continue;
                await axios.put(`${API}/vehicles/${selectedVehicle.id}/assign-personnel`, {
                    personnel_name: u.name, personnel_id: u.id, role: u.role, is_driver: false
                }, { headers });
            }
            const total = (selectedDriverId ? 1 : 0) + selectedUserIds.filter(id => id !== selectedDriverId).length;
            toast.success(`${total} personne(s) assignee(s)`);
            setPersonnelDialogOpen(false);
            setSelectedUserIds([]);
            setSelectedDriverId('');
            fetchVehicles();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const handleRemovePersonnel = async (vehicleId, personnelName) => {
        try {
            await axios.put(`${API}/vehicles/${vehicleId}/remove-personnel?personnel_name=${encodeURIComponent(personnelName)}`, {}, { headers });
            toast.success('Personnel retire');
            fetchVehicles();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Supprimer ce vehicule ?')) return;
        try {
            await axios.delete(`${API}/vehicles/${id}`, { headers });
            toast.success('Vehicule supprime');
            fetchVehicles();
        } catch (error) {
            toast.error('Erreur');
        }
    };

    const resetForm = () => {
        setFormData({ name: '', type: 'voiture', plate_number: '', capacity: 4, notes: '' });
    };

    const vehiclesWithGPS = vehicles.filter(v => v.gps_latitude && v.gps_longitude);
    const countByStatus = (s) => vehicles.filter(v => v.status === s).length;

    const getVehicleMaterialRequests = (vehicleId) => {
        const activeStatuses = ['pending', 'en_attente_reponse', 'approved', 'in_transit'];
        return materialRequests.filter(r => r.assigned_vehicle_id === vehicleId && activeStatuses.includes(r.status));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="vehicles-page">
            <div className="flex items-center justify-between">
                <div className="page-header">
                    <h1 className="text-3xl font-bold text-white">Gestion des Vehicules</h1>
                    <p className="page-subtitle">Suivi des vecteurs et du personnel</p>
                </div>
                {canManageVehicles && (
                    <Button onClick={() => { resetForm(); setEditingVehicle(null); setDialogOpen(true); }} className="tactical-btn" data-testid="add-vehicle-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Nouveau Vehicule
                    </Button>
                )}
            </div>

            {/* Vehicle Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingVehicle(null); resetForm(); } }}>
                <DialogContent className="max-w-md bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold text-white">{editingVehicle ? 'Modifier le vehicule' : 'Ajouter un vehicule'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Nom / Identifiant</Label>
                                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="tactical-input" placeholder="Ex: VSAB 01" required />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Type</Label>
                                <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                                    <SelectTrigger className="tactical-input"><SelectValue /></SelectTrigger>
                                    <SelectContent className="bg-zinc-900 border-zinc-800">
                                        {vehicleTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Immatriculation</Label>
                                <Input value={formData.plate_number} onChange={(e) => setFormData({ ...formData, plate_number: e.target.value })} className="tactical-input" placeholder="AA-123-BB" required />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Capacite</Label>
                                <Input type="number" value={formData.capacity} onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })} className="tactical-input" min="1" required />
                            </div>
                        </div>
                        <div className="form-group">
                            <Label className="form-label">Notes</Label>
                            <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="tactical-input" />
                        </div>
                        <div className="flex justify-end gap-2 pt-4">
                            <Button type="button" variant="ghost" onClick={() => { setDialogOpen(false); setEditingVehicle(null); resetForm(); }}>Annuler</Button>
                            <Button type="submit" className="tactical-btn" data-testid="vehicle-submit-btn">{editingVehicle ? 'Enregistrer' : 'Ajouter'}</Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Personnel Dialog */}
            <Dialog open={personnelDialogOpen} onOpenChange={(open) => { setPersonnelDialogOpen(open); if (!open) { setSelectedUserIds([]); setSelectedDriverId(''); } }}>
                <DialogContent className="max-w-md bg-zinc-900 border-zinc-800 max-h-[85vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>Assigner du personnel - {selectedVehicle?.name}</DialogTitle></DialogHeader>
                    {selectedVehicle && (() => {
                        const currentCount = selectedVehicle.assigned_personnel?.length || 0;
                        const capacity = selectedVehicle.capacity || 4;
                        const remainingSlots = capacity - currentCount;
                        const hasDriver = (selectedVehicle.assigned_personnel || []).some(p => p.is_driver);
                        const availableUsers = registeredUsers.filter(u => !(selectedVehicle.assigned_personnel || []).some(p => p.personnel_name === u.name));
                        const newSelectionCount = (selectedDriverId && !hasDriver ? 1 : 0) + selectedUserIds.filter(id => id !== selectedDriverId).length;
                        const canAddMore = newSelectionCount < remainingSlots;

                        return (
                            <div className="space-y-4">
                                <div className="flex items-center justify-between p-2 bg-zinc-800/50 rounded text-xs">
                                    <span className="text-zinc-400">Places occupees</span>
                                    <span className={`font-mono font-bold ${currentCount + newSelectionCount >= capacity ? 'text-red-400' : 'text-emerald-400'}`}>
                                        {currentCount + newSelectionCount} / {capacity}
                                    </span>
                                </div>

                                {/* Step 1: Driver */}
                                {!hasDriver && (
                                    <div data-testid="driver-section">
                                        <Label className="form-label flex items-center gap-2 mb-2">
                                            <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-[10px] font-bold">1</span>
                                            Conducteur
                                        </Label>
                                        <Select value={selectedDriverId} onValueChange={setSelectedDriverId}>
                                            <SelectTrigger className="tactical-input" data-testid="driver-select">
                                                <SelectValue placeholder="Choisir le conducteur" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-zinc-900 border-zinc-800 max-h-[200px]">
                                                {availableUsers.map(u => (
                                                    <SelectItem key={u.id} value={u.id}>{u.name} <span className="text-zinc-500">({u.role})</span></SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                                {hasDriver && (
                                    <div className="p-2 bg-amber-500/10 border border-amber-500/20 rounded text-sm flex items-center gap-2">
                                        <span className="w-5 h-5 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-[10px] font-bold">C</span>
                                        <span className="text-amber-300">Conducteur : {(selectedVehicle.assigned_personnel || []).find(p => p.is_driver)?.personnel_name}</span>
                                    </div>
                                )}

                                {/* Step 2: Passengers */}
                                <div data-testid="passengers-section">
                                    <Label className="form-label flex items-center gap-2 mb-2">
                                        <span className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-[10px] font-bold">{hasDriver ? '1' : '2'}</span>
                                        Passagers {remainingSlots - (selectedDriverId && !hasDriver ? 1 : 0) > 0 ? `(${remainingSlots - (selectedDriverId && !hasDriver ? 1 : 0)} place(s) restante(s))` : ''}
                                    </Label>
                                    <div className="max-h-[220px] overflow-y-auto space-y-1 border border-zinc-700 rounded-lg p-2" data-testid="personnel-multi-select">
                                        {availableUsers
                                            .filter(u => u.id !== selectedDriverId)
                                            .map(u => {
                                                const checked = selectedUserIds.includes(u.id);
                                                const disabled = !checked && !canAddMore;
                                                return (
                                                    <label key={u.id} className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'} ${checked ? 'bg-blue-500/10 border border-blue-500/30' : 'hover:bg-zinc-800 border border-transparent'}`}>
                                                        <input type="checkbox" checked={checked} disabled={disabled}
                                                            onChange={(e) => { if (e.target.checked) setSelectedUserIds([...selectedUserIds, u.id]); else setSelectedUserIds(selectedUserIds.filter(x => x !== u.id)); }}
                                                            className="accent-blue-500" data-testid={`personnel-check-${u.id}`} />
                                                        <div className="flex-1">
                                                            <p className="text-sm text-white font-medium">{u.name}</p>
                                                            <p className="text-xs text-zinc-500">{u.role}</p>
                                                        </div>
                                                    </label>
                                                );
                                            })}
                                        {availableUsers.filter(u => u.id !== selectedDriverId).length === 0 && (
                                            <p className="text-zinc-500 text-xs text-center py-2">Aucun utilisateur disponible</p>
                                        )}
                                    </div>
                                </div>

                                {newSelectionCount > 0 && <p className="text-xs text-blue-400">{newSelectionCount} personne(s) selectionnee(s)</p>}
                                {remainingSlots <= 0 && <p className="text-xs text-red-400">Vehicule complet - aucune place disponible</p>}

                                <div className="flex justify-end gap-2">
                                    <Button variant="ghost" onClick={() => { setPersonnelDialogOpen(false); setSelectedUserIds([]); setSelectedDriverId(''); }}>Annuler</Button>
                                    <Button onClick={handleAddPersonnel} className="tactical-btn" disabled={newSelectionCount === 0 || remainingSlots <= 0} data-testid="personnel-submit-btn">
                                        Assigner ({newSelectionCount})
                                    </Button>
                                </div>
                            </div>
                        );
                    })()}
                </DialogContent>
            </Dialog>

            {/* Location Dialog */}
            <Dialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen}>
                <DialogContent className="max-w-md bg-zinc-900 border-zinc-800">
                    <DialogHeader><DialogTitle className="text-white">Mettre a jour la position - {selectedVehicle?.name}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <p className="text-xs text-zinc-400">Saisissez les coordonnees GPS ou une localisation manuelle</p>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Latitude</Label>
                                <Input type="number" step="any" value={locationData.gps_latitude} onChange={(e) => setLocationData({...locationData, gps_latitude: e.target.value})} className="tactical-input" placeholder="Ex: 43.5298" data-testid="location-latitude" />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Longitude</Label>
                                <Input type="number" step="any" value={locationData.gps_longitude} onChange={(e) => setLocationData({...locationData, gps_longitude: e.target.value})} className="tactical-input" placeholder="Ex: 5.4484" data-testid="location-longitude" />
                            </div>
                        </div>
                        <div className="relative flex items-center py-2">
                            <div className="flex-grow border-t border-zinc-700"></div>
                            <span className="flex-shrink mx-3 text-zinc-500 text-xs">ou</span>
                            <div className="flex-grow border-t border-zinc-700"></div>
                        </div>
                        <div className="form-group">
                            <Label className="form-label">Localisation manuelle</Label>
                            <Input value={locationData.manual_location} onChange={(e) => setLocationData({...locationData, manual_location: e.target.value})} className="tactical-input" placeholder="Ex: Devant la mairie" data-testid="location-manual" />
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="ghost" onClick={() => setLocationDialogOpen(false)}>Annuler</Button>
                            <Button onClick={handleUpdateLocation} className="tactical-btn" data-testid="location-submit"><MapPin className="w-4 h-4 mr-2" />Mettre a jour</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Mission Dialog */}
            <Dialog open={missionDialogOpen} onOpenChange={setMissionDialogOpen}>
                <DialogContent className="max-w-sm bg-zinc-900 border-zinc-800">
                    <DialogHeader><DialogTitle className="text-white">Assigner une mission - {selectedVehicle?.name}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div className="form-group">
                            <Label className="form-label">Description de la mission</Label>
                            <Textarea value={missionText} onChange={(e) => setMissionText(e.target.value)} className="tactical-input" placeholder="Ex: Transport evacues secteur Nord vers CARE Gymnase" data-testid="mission-text" />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="ghost" onClick={() => setMissionDialogOpen(false)}>Annuler</Button>
                            <Button onClick={handleSetMission} className="tactical-btn" data-testid="mission-submit">Depart en mission</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Problem Report Dialog */}
            <Dialog open={problemDialogOpen} onOpenChange={setProblemDialogOpen}>
                <DialogContent className="max-w-md bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-orange-500" />
                            Declarer un probleme - {selectedVehicle?.name}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="form-group">
                            <Label className="form-label">Description du probleme</Label>
                            <Textarea
                                value={problemData.description}
                                onChange={(e) => setProblemData({ ...problemData, description: e.target.value })}
                                className="tactical-input"
                                placeholder="Decrivez le probleme rencontre..."
                                rows={3}
                                data-testid="problem-description"
                            />
                        </div>
                        <div className="form-group">
                            <Label className="form-label">Gravite</Label>
                            <div className="grid grid-cols-2 gap-3 mt-2">
                                <button
                                    type="button"
                                    onClick={() => setProblemData({ ...problemData, severity: 'maintenance' })}
                                    className={`p-3 rounded-lg border text-left transition-colors ${problemData.severity === 'maintenance' ? 'bg-orange-500/10 border-orange-500/50' : 'bg-zinc-800/50 border-zinc-700/50 hover:border-zinc-600'}`}
                                    data-testid="severity-maintenance"
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        <Wrench className="w-4 h-4 text-orange-400" />
                                        <span className="text-sm font-medium text-white">Maintenance</span>
                                    </div>
                                    <p className="text-[11px] text-zinc-400">Anomalie necessitant une reparation, vehicule temporairement indisponible</p>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setProblemData({ ...problemData, severity: 'en_panne' })}
                                    className={`p-3 rounded-lg border text-left transition-colors ${problemData.severity === 'en_panne' ? 'bg-red-500/10 border-red-500/50' : 'bg-zinc-800/50 border-zinc-700/50 hover:border-zinc-600'}`}
                                    data-testid="severity-en-panne"
                                >
                                    <div className="flex items-center gap-2 mb-1">
                                        <XCircle className="w-4 h-4 text-red-400" />
                                        <span className="text-sm font-medium text-white">En panne</span>
                                    </div>
                                    <p className="text-[11px] text-zinc-400">Anomalie importante causant l'immobilisation du vehicule</p>
                                </button>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="ghost" onClick={() => setProblemDialogOpen(false)}>Annuler</Button>
                            <Button onClick={handleReportProblem} className="bg-orange-600 hover:bg-orange-700 text-white" disabled={!problemData.description} data-testid="problem-submit">
                                <AlertTriangle className="w-4 h-4 mr-2" />
                                Declarer le probleme
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Status Change Dialog */}
            <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
                <DialogContent className="max-w-sm bg-zinc-900 border-zinc-800">
                    <DialogHeader><DialogTitle className="text-white">Changer le statut - {selectedVehicle?.name}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            {Object.entries(statusConfig).map(([key, cfg]) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setNewStatus(key)}
                                    className={`w-full p-3 rounded-lg border text-left transition-colors flex items-center gap-3 ${newStatus === key ? 'border-blue-500/50 bg-blue-500/5' : 'bg-zinc-800/50 border-zinc-700/50 hover:border-zinc-600'}`}
                                    data-testid={`status-option-${key}`}
                                >
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cfg.dot }}></div>
                                    <span className="text-sm text-white">{cfg.label}</span>
                                </button>
                            ))}
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="ghost" onClick={() => setStatusDialogOpen(false)}>Annuler</Button>
                            <Button onClick={handleChangeStatus} className="tactical-btn" disabled={!newStatus} data-testid="status-submit">Confirmer</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Vehicle Detail Dialog */}
            <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                <DialogContent className="max-w-lg bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Truck className="w-5 h-5 text-blue-500" /> {selectedVehicle?.name}
                        </DialogTitle>
                    </DialogHeader>
                    {selectedVehicle && (
                        <div className="space-y-4" data-testid="vehicle-detail">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="p-3 bg-zinc-800/50 rounded">
                                    <p className="text-xs text-zinc-500">Type</p>
                                    <p className="text-white text-sm">{vehicleTypes.find(t => t.value === selectedVehicle.type)?.label || selectedVehicle.type}</p>
                                </div>
                                <div className="p-3 bg-zinc-800/50 rounded">
                                    <p className="text-xs text-zinc-500">Immatriculation</p>
                                    <p className="text-white text-sm font-mono">{selectedVehicle.plate_number}</p>
                                </div>
                            </div>
                            <div className="p-3 bg-zinc-800/50 rounded">
                                <p className="text-xs text-zinc-500 mb-1">Statut</p>
                                <Badge className={`${getStatusConfig(selectedVehicle.status).color} text-xs`}>{getStatusConfig(selectedVehicle.status).label}</Badge>
                            </div>
                            {selectedVehicle.current_mission && (
                                <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded">
                                    <p className="text-xs text-zinc-500 mb-1">Mission actuelle</p>
                                    <p className="text-amber-400 text-sm">{selectedVehicle.current_mission}</p>
                                </div>
                            )}
                            {selectedVehicle.problem_report && (
                                <div className="p-3 bg-red-500/5 border border-red-500/20 rounded">
                                    <p className="text-xs text-zinc-500 mb-1">Probleme declare</p>
                                    <p className="text-red-400 text-sm">{selectedVehicle.problem_report.description}</p>
                                    <p className="text-[11px] text-zinc-500 mt-1">
                                        Par {selectedVehicle.problem_report.reported_by} le {new Date(selectedVehicle.problem_report.reported_at).toLocaleString('fr-FR')}
                                    </p>
                                    {canManageVehicles && (
                                        <Button size="sm" onClick={() => handleResolveProblem(selectedVehicle.id)} className="mt-2 bg-green-600 hover:bg-green-700 text-white h-7 text-xs" data-testid="resolve-problem-detail">
                                            <CheckCircle className="w-3 h-3 mr-1" />Marquer comme resolu
                                        </Button>
                                    )}
                                </div>
                            )}
                            <div className="p-3 bg-zinc-800/50 rounded">
                                <p className="text-xs text-zinc-500 mb-1">Position</p>
                                {selectedVehicle.gps_latitude && selectedVehicle.gps_longitude ? (
                                    <div>
                                        <p className="text-white text-sm font-mono">{selectedVehicle.gps_latitude.toFixed(4)}, {selectedVehicle.gps_longitude.toFixed(4)}</p>
                                        <GpsFreshness lastUpdate={selectedVehicle.last_gps_update} />
                                        {selectedVehicle.last_gps_update && <p className="text-xs text-zinc-500">MAJ: {new Date(selectedVehicle.last_gps_update).toLocaleString('fr-FR')}</p>}
                                    </div>
                                ) : selectedVehicle.manual_location ? (
                                    <p className="text-white text-sm">{selectedVehicle.manual_location}</p>
                                ) : (
                                    <p className="text-zinc-500 text-sm">Non renseignee</p>
                                )}
                            </div>
                            <div className="p-3 bg-zinc-800/50 rounded">
                                <p className="text-xs text-zinc-500 mb-2">Personnel ({selectedVehicle.assigned_personnel?.length || 0}/{selectedVehicle.capacity})</p>
                                {selectedVehicle.assigned_personnel?.length > 0 ? (
                                    <div className="space-y-1">
                                        {selectedVehicle.assigned_personnel.map((p) => (
                                            <div key={p.personnel_id || p.personnel_name} className="flex items-center justify-between text-sm">
                                                <span className="flex items-center gap-2">
                                                    {p.is_driver ? (
                                                        <span className="w-4 h-4 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-[8px] font-bold shrink-0">C</span>
                                                    ) : (
                                                        <span className="w-4 h-4 rounded-full bg-zinc-700 text-zinc-400 flex items-center justify-center text-[8px] shrink-0">P</span>
                                                    )}
                                                    <span className="text-white">{p.personnel_name}</span>
                                                </span>
                                                <span className="text-zinc-500">{p.is_driver ? 'Conducteur' : p.role || 'Passager'}</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-zinc-500 text-sm">Aucun personnel</p>
                                )}
                            </div>
                            {/* Material requests assigned to this vehicle */}
                            {(() => {
                                const vehicleMR = getVehicleMaterialRequests(selectedVehicle.id);
                                return (
                                    <div className="p-3 bg-zinc-800/50 rounded" data-testid="vehicle-material-requests">
                                        <p className="text-xs text-zinc-500 mb-2 flex items-center gap-1">
                                            <ClipboardList className="w-3 h-3" />
                                            Materiel assigne ({vehicleMR.length})
                                        </p>
                                        {vehicleMR.length > 0 ? (
                                            <div className="space-y-2">
                                                {vehicleMR.map(req => {
                                                    const urgencyColor = req.urgency === 'critique' ? 'text-red-400' : req.urgency === 'urgent' ? 'text-amber-400' : 'text-blue-400';
                                                    const statusLabels = { pending: 'En attente', en_attente_reponse: 'Att. reponse', approved: 'Approuvee', in_transit: 'En transit' };
                                                    return (
                                                        <div key={req.id} className="p-2 bg-zinc-900/60 rounded border border-zinc-700/50" data-testid={`vehicle-mr-${req.id}`}>
                                                            <div className="flex items-center justify-between mb-1">
                                                                <span className="text-xs text-white font-medium">{req.center_name}</span>
                                                                <span className={`text-[10px] ${urgencyColor} font-semibold uppercase`}>{req.urgency}</span>
                                                            </div>
                                                            <div className="space-y-0.5">
                                                                {req.items.map((item) => (
                                                                    <p key={`${item.resource_type}-${item.quantity}`} className="text-xs text-zinc-300">
                                                                        <span className="text-white font-mono">{item.quantity}x</span> {item.resource_type}{item.notes ? ` (${item.notes})` : ''}
                                                                    </p>
                                                                ))}
                                                            </div>
                                                            <p className="text-[10px] text-zinc-500 mt-1">
                                                                Statut: <span className="text-cyan-400">{statusLabels[req.status] || req.status}</span> — par {req.requested_by_name}
                                                            </p>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <p className="text-zinc-500 text-sm">Aucune demande de materiel</p>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                <Card className="stat-card border-l-blue-500">
                    <CardContent className="p-0">
                        <p className="stat-value">{vehicles.length}</p>
                        <p className="stat-label">Total</p>
                    </CardContent>
                </Card>
                <Card className="stat-card border-l-green-500">
                    <CardContent className="p-0">
                        <p className="stat-value">{countByStatus('disponible')}</p>
                        <p className="stat-label">Disponibles</p>
                    </CardContent>
                </Card>
                <Card className="stat-card border-l-amber-500">
                    <CardContent className="p-0">
                        <p className="stat-value">{countByStatus('en_mission')}</p>
                        <p className="stat-label">En mission</p>
                    </CardContent>
                </Card>
                <Card className="stat-card border-l-sky-500">
                    <CardContent className="p-0">
                        <p className="stat-value">{countByStatus('en_transit')}</p>
                        <p className="stat-label">En transit</p>
                    </CardContent>
                </Card>
                <Card className="stat-card border-l-orange-500">
                    <CardContent className="p-0">
                        <p className="stat-value">{countByStatus('maintenance') + countByStatus('en_panne')}</p>
                        <p className="stat-label">HS / Panne</p>
                    </CardContent>
                </Card>
                <Card className="stat-card border-l-purple-500">
                    <CardContent className="p-0">
                        <p className="stat-value">{countByStatus('reapprovisionnement')}</p>
                        <p className="stat-label">Reappro.</p>
                    </CardContent>
                </Card>
            </div>

            {/* Map */}
            {vehiclesWithGPS.length > 0 && (
                <Card className="content-card">
                    <CardHeader className="card-header">
                        <CardTitle className="card-title flex items-center gap-2">
                            <Navigation className="w-5 h-5 text-blue-500" />
                            Localisation GPS en temps reel
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <div className="h-[300px]">
                            <MapContainer center={[vehiclesWithGPS[0].gps_latitude, vehiclesWithGPS[0].gps_longitude]} zoom={10} className="h-full w-full">
                                <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                                {vehiclesWithGPS.map(v => (
                                    <CircleMarker
                                        key={v.id}
                                        center={[v.gps_latitude, v.gps_longitude]}
                                        radius={10}
                                        pathOptions={{ color: getStatusConfig(v.status).dot, fillColor: getStatusConfig(v.status).dot, fillOpacity: 0.8 }}
                                    >
                                        <Popup>
                                            <div className="p-2">
                                                <p className="font-bold">{v.name}</p>
                                                <p className="text-sm">{v.current_mission || 'Pas de mission'}</p>
                                                <GpsFreshness lastUpdate={v.last_gps_update} className="mt-1" />
                                            </div>
                                        </Popup>
                                    </CircleMarker>
                                ))}
                            </MapContainer>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Vehicles Table */}
            <Card className="content-card">
                <CardContent className="p-0 overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-zinc-800 hover:bg-transparent">
                                <TableHead className="text-zinc-400 w-[160px]">Vehicule</TableHead>
                                <TableHead className="text-zinc-400 w-[90px]">Type</TableHead>
                                <TableHead className="text-zinc-400 w-[140px]">Statut</TableHead>
                                <TableHead className="text-zinc-400 min-w-[220px]">Mission</TableHead>
                                <TableHead className="text-zinc-400">Personnel</TableHead>
                                <TableHead className="text-zinc-400 w-[100px]">Position</TableHead>
                                <TableHead className="text-zinc-400 text-right w-[200px]">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {vehicles.map(vehicle => {
                                const sc = getStatusConfig(vehicle.status);
                                return (
                                    <TableRow key={vehicle.id} className="border-zinc-800 cursor-pointer hover:bg-zinc-800/50" onClick={() => { setSelectedVehicle(vehicle); setDetailDialogOpen(true); }} data-testid={`vehicle-row-${vehicle.id}`}>
                                        <TableCell>
                                            <div>
                                                <p className="font-medium text-white">{vehicle.name}</p>
                                                <p className="text-xs text-zinc-500 font-mono">{vehicle.plate_number}</p>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-zinc-300 text-sm">
                                            {vehicleTypes.find(t => t.value === vehicle.type)?.label || vehicle.type}
                                        </TableCell>
                                        <TableCell>
                                            <Badge className={`${sc.color} text-[11px] whitespace-nowrap`} data-testid={`status-badge-${vehicle.id}`}>
                                                {sc.label}
                                            </Badge>
                                            {vehicle.problem_report && (
                                                <p className="text-[10px] text-red-400 mt-1 truncate max-w-[130px]" title={vehicle.problem_report.description}>
                                                    {vehicle.problem_report.description}
                                                </p>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {vehicle.current_mission ? (
                                                <div className="max-w-[280px]">
                                                    <p className="text-sm text-amber-400 whitespace-pre-wrap break-words" data-testid={`mission-detail-${vehicle.id}`}>
                                                        {vehicle.current_mission}
                                                    </p>
                                                </div>
                                            ) : (
                                                <span className="text-zinc-600 text-sm">-</span>
                                            )}
                                            {(() => {
                                                const mrCount = getVehicleMaterialRequests(vehicle.id).length;
                                                if (mrCount === 0) return null;
                                                return (
                                                    <div className="flex items-center gap-1 mt-1" data-testid={`vehicle-mr-badge-${vehicle.id}`}>
                                                        <Package className="w-3 h-3 text-purple-400" />
                                                        <span className="text-[10px] text-purple-400 font-medium">{mrCount} demande{mrCount > 1 ? 's' : ''} materiel</span>
                                                    </div>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-wrap gap-1">
                                                {vehicle.assigned_personnel.map((p, idx) => (
                                                    <span key={p.personnel_id || p.personnel_name} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${p.is_driver ? 'bg-amber-500/15 border border-amber-500/30' : 'bg-zinc-800'}`} data-testid={`personnel-tag-${vehicle.id}-${idx}`}>
                                                        {p.is_driver && <span className="text-amber-400 text-[9px] font-bold">C</span>}
                                                        {p.personnel_name} <span className="text-zinc-500">({p.is_driver ? 'Conducteur' : p.role})</span>
                                                        {canManageVehicles && (
                                                            <button onClick={(e) => { e.stopPropagation(); handleRemovePersonnel(vehicle.id, p.personnel_name); }} className="text-red-400 hover:text-red-300 ml-0.5">
                                                                <UserMinus className="w-3 h-3" />
                                                            </button>
                                                        )}
                                                    </span>
                                                ))}
                                                {vehicle.assigned_personnel.length === 0 && <span className="text-zinc-600 text-sm">-</span>}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-zinc-400 text-xs font-mono">
                                            {vehicle.gps_latitude && vehicle.gps_longitude ? (
                                                <span className="flex flex-col gap-0.5">
                                                    <span className="flex items-center gap-1 text-green-400"><MapPin className="w-3 h-3" />GPS</span>
                                                    <GpsFreshness lastUpdate={vehicle.last_gps_update} />
                                                </span>
                                            ) : vehicle.manual_location ? (
                                                <span className="truncate max-w-[90px] block">{vehicle.manual_location}</span>
                                            ) : '-'}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                                                {canManageVehicles && (
                                                    <>
                                                        <Button size="sm" variant="ghost" onClick={() => handleEditVehicle(vehicle)} className="text-zinc-400 h-8 w-8 p-0" title="Modifier" data-testid={`edit-vehicle-btn-${vehicle.id}`}>
                                                            <Edit className="w-3.5 h-3.5" />
                                                        </Button>
                                                        <Button size="sm" variant="ghost" onClick={() => { setSelectedVehicle(vehicle); setNewStatus(vehicle.status); setStatusDialogOpen(true); }} className="text-cyan-400 h-8 w-8 p-0" title="Changer statut" data-testid={`status-btn-${vehicle.id}`}>
                                                            <CheckCircle className="w-3.5 h-3.5" />
                                                        </Button>
                                                        <Button size="sm" variant="ghost" onClick={() => { setSelectedVehicle(vehicle); setMissionText(vehicle.current_mission || ''); setMissionDialogOpen(true); }} className="text-amber-400 h-8 w-8 p-0" title="Mission" data-testid={`mission-btn-${vehicle.id}`}>
                                                            <Navigation className="w-3.5 h-3.5" />
                                                        </Button>
                                                        <Button size="sm" variant="ghost" onClick={() => { setSelectedVehicle(vehicle); setProblemData({ description: '', severity: 'maintenance' }); setProblemDialogOpen(true); }} className="text-orange-400 h-8 w-8 p-0" title="Declarer probleme" data-testid={`problem-btn-${vehicle.id}`}>
                                                            <AlertTriangle className="w-3.5 h-3.5" />
                                                        </Button>
                                                        <Button size="sm" variant="ghost" onClick={() => { setSelectedVehicle(vehicle); setLocationData({ gps_latitude: vehicle.gps_latitude || '', gps_longitude: vehicle.gps_longitude || '', manual_location: vehicle.manual_location || '' }); setLocationDialogOpen(true); }} className="text-green-400 h-8 w-8 p-0" title="Position GPS" data-testid={`location-btn-${vehicle.id}`}>
                                                            <MapPin className="w-3.5 h-3.5" />
                                                        </Button>
                                                        <Button size="sm" variant="ghost" onClick={() => { setSelectedVehicle(vehicle); setPersonnelDialogOpen(true); }} className="text-blue-400 h-8 w-8 p-0" title="Personnel" data-testid={`personnel-btn-${vehicle.id}`}>
                                                            <UserPlus className="w-3.5 h-3.5" />
                                                        </Button>
                                                        <Button size="sm" variant="ghost" onClick={() => handleDelete(vehicle.id)} className="text-red-500 h-8 w-8 p-0" title="Supprimer" data-testid={`delete-btn-${vehicle.id}`}>
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {vehicles.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-8 text-zinc-500">
                                        <Truck className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                        Aucun vehicule enregistre
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
};

export default VehiclesPage;
